import { useQuery } from "@apollo/client";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import SearchIcon from "@mui/icons-material/Search";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import {
  Alert,
  Box,
  Button,
  ButtonBase,
  CircularProgress,
  InputAdornment,
  Link,
  MenuItem,
  type SxProps,
  TextField,
  type Theme,
  Typography,
} from "@mui/material";
import { type ReactNode, useEffect, useState } from "react";
import { Title, useTranslate } from "react-admin";
import { Navigate, useSearchParams } from "react-router-dom";

import { CrmCard } from "../components/crm/CrmCard";
import { CrmPageHeader } from "../components/crm/CrmPageHeader";
import { ListPagination } from "../components/ListPagination";
import { InventoryHexnode, inventoryHexnodeUrl } from "../components/logistics/InventoryHexnode";
import { InventoryTable } from "../components/logistics/InventoryTable";
import { inventoryTypeColor, InventoryTypeIcon } from "../components/logistics/InventoryTypeIcon";
import { InventoryWorkspaceOne, inventoryWorkspaceOneUrl } from "../components/logistics/InventoryWorkspaceOne";
import { RequirePermission } from "../components/RequirePermission";
import { LOGISTICS_INVENTORY_PAGE_SIZE as PAGE_SIZE } from "../constants/pagination";
import { ResourceName } from "../constants/resources";
import {
  LogisticsInventoryDeviceFilter,
  LogisticsInventoryDeviceFilterProvider,
  LogisticsInventoryDeviceFilterSourceState,
  LogisticsInventoryDocument,
  type LogisticsInventoryQuery,
  type LogisticsInventoryQueryVariables,
  LogisticsInventoryStockState,
  LogisticsWorkspaceOneDevicesDocument,
  type LogisticsWorkspaceOneDevicesQuery,
} from "../generated/graphql";
import { CRM_COLORS, formatCrmCount, formatCrmDateTime, formatCrmDayOf } from "../lib/crm";
import { groupInventoryCounts, type InventoryTypeCount, simLastOnlineText } from "../lib/logistics";
import { WorkspaceOneInventoryContext, WorkspaceOneInventoryOverview } from "./LogisticsWorkspaceOnePage";

type Inventory = LogisticsInventoryQuery["logisticsInventory"];
type Row = Inventory["rows"][number];
type Stock = Inventory["stock"][number];

const SEARCH_DEBOUNCE_MS = 300;
// The offset is a GraphQL Int.
const MAX_PAGE = Math.floor(2147483647 / PAGE_SIZE);
const ALL = "";
const INACTIVE_DAY_PRESETS = [7, 14, 30, 90];
const DEFAULT_INACTIVE_DAYS = 30;

const deviceFilterOf = (value: string | null): LogisticsInventoryDeviceFilter | null =>
  Object.values(LogisticsInventoryDeviceFilter).find(filter => filter === value) ?? null;

const DeviceFilterCoverage = ({ sources }: { sources: Inventory["deviceFilterSources"] }) => {
  const translate = useTranslate();
  return (
    <>
      {sources.map(source => {
        const provider = translate(`logistics.inventory.deviceFilters.providers.${source.provider}`);
        const time = source.syncedAt ? formatCrmDateTime(source.syncedAt) : translate("logistics.workspaceOne.unknown");
        const incomplete = [
          LogisticsInventoryDeviceFilterSourceState.NotConfigured,
          LogisticsInventoryDeviceFilterSourceState.Unavailable,
          LogisticsInventoryDeviceFilterSourceState.NotScanned,
        ].includes(source.state);
        return (
          <Box key={source.provider}>
            {incomplete && (
              <Alert severity="warning" sx={{ mb: "12px" }}>
                {translate("logistics.inventory.deviceFilters.incomplete", {
                  provider,
                  state: translate(`logistics.inventory.deviceFilters.sourceStates.${source.state}`),
                })}
              </Alert>
            )}
            {source.stale && (
              <Alert severity="warning" sx={{ mb: "12px" }}>
                {translate("logistics.inventory.deviceFilters.stale", { provider, time })}
              </Alert>
            )}
            {source.provider === LogisticsInventoryDeviceFilterProvider.Whereversim &&
              source.state === LogisticsInventoryDeviceFilterSourceState.Cached && (
                <Alert severity="info" sx={{ mb: "12px" }}>
                  {translate("logistics.inventory.deviceFilters.cachedSim", { time })}
                </Alert>
              )}
          </Box>
        );
      })}
    </>
  );
};

type Selection = {
  type: string | null;
  status: string | null;
  stockState: LogisticsInventoryStockState | null;
};

// An address value that is no stock state is none.
const stockStateOf = (value: string | null): LogisticsInventoryStockState | null =>
  Object.values(LogisticsInventoryStockState).find(stockState => stockState === value) ?? null;

// A pressed count: a light brand tint with dark brand text, which keeps AA contrast.
const PRESSED_TINT = "#eef4fc";

// The shared look of a stock or status row; a pressed row is marked by the
// brand colour, a tint and an edge.
const rowSx = (pressed: boolean) => ({
  display: "flex",
  alignItems: "center",
  gap: "8px",
  width: "100%",
  px: "14px",
  py: "5px",
  textAlign: "left",
  fontSize: "13px",
  lineHeight: 1.35,
  color: pressed ? CRM_COLORS.pathCurrent : CRM_COLORS.text,
  fontWeight: pressed ? 700 : 400,
  backgroundColor: pressed ? PRESSED_TINT : "transparent",
  boxShadow: pressed ? `inset 3px 0 0 ${CRM_COLORS.brand}` : "none",
  "&:hover": { backgroundColor: pressed ? PRESSED_TINT : CRM_COLORS.rowHover },
  "&.Mui-focusVisible": { outline: `2px solid ${CRM_COLORS.brand}`, outlineOffset: "-2px" },
});

const labelSx = { flex: 1, minWidth: 0 } as const;
const countSx = { fontVariantNumeric: "tabular-nums", fontWeight: 600, marginLeft: "auto" } as const;

// The free units of a type; a Tablet or TV Box splits them into ready to ship
// and needing staging. In Stock units that their history still places at a
// client are a case to clean up in Salesforce, shown only when there are any.
// Each count opens the units it counts, as a status count does.
const StockRows = ({
  type,
  typeLabel,
  stock,
  selected,
  onSelect,
}: {
  type: string | null;
  typeLabel: string;
  stock: Stock | undefined;
  selected: Selection;
  onSelect: (selection: Selection) => void;
}) => {
  const translate = useTranslate();
  const isPressed = (stockState: LogisticsInventoryStockState) =>
    type !== null && selected.type === type && selected.stockState === stockState;
  const row = ({
    key,
    stockState,
    label,
    ariaLabelKey,
    count,
    marker,
    sx = [],
  }: {
    key: string;
    stockState: LogisticsInventoryStockState;
    label: string;
    ariaLabelKey: string;
    count: number;
    marker?: ReactNode;
    sx?: SxProps<Theme>;
  }) => {
    const pressed = isPressed(stockState);
    return (
      <ButtonBase
        key={key}
        data-testid={`inventory-${key}-count`}
        disabled={type === null}
        aria-pressed={pressed}
        aria-label={translate(`logistics.inventory.stock.${ariaLabelKey}`, {
          type: typeLabel,
          count: formatCrmCount(count),
          smart_count: count,
        })}
        onClick={() => onSelect({ type, status: null, stockState })}
        sx={[rowSx(pressed), ...(Array.isArray(sx) ? sx : [sx])]}
      >
        {marker}
        <Box component="span" sx={labelSx}>
          {label}
        </Box>
        <Box component="span" sx={countSx}>
          {formatCrmCount(count)}
        </Box>
      </ButtonBase>
    );
  };
  const split = stock?.readyToShip != null && stock.needsStaging != null;
  const atClient = stock?.inStockAtClient ?? 0;
  const subRowSx = (stockState: LogisticsInventoryStockState) => ({
    pl: "28px",
    fontSize: "12px",
    ...(isPressed(stockState) ? {} : { color: CRM_COLORS.textWeak }),
  });
  const dot = (color: string) => (
    <Box
      component="span"
      aria-hidden
      sx={{ width: 7, height: 7, borderRadius: "50%", flex: "none", backgroundColor: color }}
    />
  );
  return (
    <>
      {row({
        key: "free",
        stockState: LogisticsInventoryStockState.Free,
        label: translate("logistics.inventory.stock.free"),
        ariaLabelKey: "showFree",
        count: stock?.free ?? 0,
      })}
      {split &&
        row({
          key: "ready",
          stockState: LogisticsInventoryStockState.ReadyToShip,
          label: translate("logistics.inventory.stock.readyToShip"),
          ariaLabelKey: "showReadyToShip",
          count: stock.readyToShip ?? 0,
          marker: dot(CRM_COLORS.success),
          sx: subRowSx(LogisticsInventoryStockState.ReadyToShip),
        })}
      {split &&
        row({
          key: "staging",
          stockState: LogisticsInventoryStockState.NeedsStaging,
          label: translate("logistics.inventory.stock.needsStaging"),
          ariaLabelKey: "showNeedsStaging",
          count: stock.needsStaging ?? 0,
          marker: dot(CRM_COLORS.warning),
          sx: subRowSx(LogisticsInventoryStockState.NeedsStaging),
        })}
      {atClient > 0 &&
        row({
          key: "at-client",
          stockState: LogisticsInventoryStockState.InStockAtClient,
          label: translate("logistics.inventory.stock.inStockAtClient"),
          ariaLabelKey: "showInStockAtClient",
          count: atClient,
          marker: (
            <WarningAmberRoundedIcon aria-hidden sx={{ fontSize: 15, color: CRM_COLORS.warning, flex: "none" }} />
          ),
          sx: { fontSize: "12px" },
        })}
    </>
  );
};

// A labelled block of rows on a card.
const CardSection = ({ label, children }: { label: string; children: ReactNode }) => (
  <Box sx={{ borderTop: `1px solid ${CRM_COLORS.cardBorder}`, py: "6px" }}>
    <Typography
      component="div"
      sx={{
        fontSize: "11px",
        fontWeight: 700,
        letterSpacing: "0.06em",
        textTransform: "uppercase",
        color: CRM_COLORS.label,
        px: "14px",
        pt: "4px",
        pb: "2px",
      }}
    >
      {label}
    </Typography>
    {children}
  </Box>
);

// One card per type: its total opens every unit of the type, each status
// count the units of the type in that status, each stock count the units in
// that stock state. Units without a type or status cannot be filtered on;
// their counts are shown only.
const InventoryCounts = ({
  groups,
  stock,
  selected,
  onSelect,
}: {
  groups: InventoryTypeCount[];
  stock: Stock[];
  selected: Selection;
  onSelect: (selection: Selection) => void;
}) => {
  const translate = useTranslate();
  const typeLabel = (type: string | null) => type ?? translate("logistics.inventory.noType");
  const statusLabel = (status: string | null) => status ?? translate("logistics.inventory.noStatus");
  return (
    <Box
      role="group"
      aria-label={translate("logistics.inventory.countsLabel")}
      sx={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))",
        gap: "12px",
        pb: "16px",
        px: "16px",
      }}
    >
      {groups.map(group => {
        const typeSelected = selected.type === group.type && selected.type !== null;
        const typeStock = stock.find(entry => entry.type === group.type);
        return (
          <Box
            key={group.type ?? "none"}
            data-testid="inventory-type-count"
            sx={{
              backgroundColor: CRM_COLORS.cardBackground,
              border: `1px solid ${typeSelected ? CRM_COLORS.brand : CRM_COLORS.cardBorder}`,
              borderRadius: "4px",
              boxShadow: typeSelected ? `0 0 0 1px ${CRM_COLORS.brand}` : CRM_COLORS.cardShadow,
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <ButtonBase
              disabled={group.type === null}
              aria-pressed={typeSelected && selected.status === null && selected.stockState === null}
              aria-label={translate("logistics.inventory.showType", {
                type: typeLabel(group.type),
                count: formatCrmCount(group.total),
                smart_count: group.total,
              })}
              onClick={() => onSelect({ type: group.type, status: null, stockState: null })}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "12px",
                width: "100%",
                p: "12px 14px",
                justifyContent: "flex-start",
                textAlign: "left",
                "&:hover": { backgroundColor: CRM_COLORS.rowHover },
                "&.Mui-focusVisible": { outline: `2px solid ${CRM_COLORS.brand}`, outlineOffset: "-2px" },
              }}
            >
              <InventoryTypeIcon type={group.type} size={36} />
              <Typography
                sx={{
                  fontSize: "14px",
                  fontWeight: 700,
                  color: CRM_COLORS.text,
                  flex: 1,
                  minWidth: 0,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {typeLabel(group.type)}
              </Typography>
              <Typography
                sx={{
                  fontSize: "24px",
                  fontWeight: 700,
                  lineHeight: 1,
                  color: CRM_COLORS.text,
                  fontVariantNumeric: "tabular-nums",
                }}
              >
                {formatCrmCount(group.total)}
              </Typography>
            </ButtonBase>
            <CardSection label={translate("logistics.inventory.sections.stock")}>
              <StockRows
                type={group.type}
                typeLabel={typeLabel(group.type)}
                stock={typeStock}
                selected={selected}
                onSelect={onSelect}
              />
            </CardSection>
            <CardSection label={translate("logistics.inventory.sections.status")}>
              {group.statuses.map(({ status, count }) => {
                const statusSelected = typeSelected && selected.status === status;
                return (
                  <ButtonBase
                    key={status ?? "none"}
                    disabled={group.type === null || status === null}
                    aria-pressed={statusSelected}
                    aria-label={translate("logistics.inventory.showStatus", {
                      type: typeLabel(group.type),
                      status: statusLabel(status),
                      count: formatCrmCount(count),
                      smart_count: count,
                    })}
                    onClick={() => onSelect({ type: group.type, status, stockState: null })}
                    sx={{ ...rowSx(statusSelected), flexDirection: "column", alignItems: "stretch", gap: "4px" }}
                  >
                    <Box component="span" sx={{ display: "flex", gap: "8px", alignItems: "baseline" }}>
                      <Box component="span" sx={labelSx}>
                        {statusLabel(status)}
                      </Box>
                      <Box component="span" sx={countSx}>
                        {formatCrmCount(count)}
                      </Box>
                    </Box>
                    <Box
                      component="span"
                      aria-hidden
                      sx={{
                        display: "block",
                        height: 4,
                        borderRadius: 2,
                        backgroundColor: CRM_COLORS.pathIncomplete,
                        overflow: "hidden",
                      }}
                    >
                      <Box
                        component="span"
                        sx={{
                          display: "block",
                          height: "100%",
                          width: `${group.total > 0 ? (count / group.total) * 100 : 0}%`,
                          backgroundColor: statusSelected ? CRM_COLORS.brand : inventoryTypeColor(group.type),
                        }}
                      />
                    </Box>
                  </ButtonBase>
                );
              })}
            </CardSection>
          </Box>
        );
      })}
    </Box>
  );
};

const InventoryOverviewTable = ({
  rows,
  overview,
}: {
  rows: Row[];
  overview?: LogisticsWorkspaceOneDevicesQuery["logisticsWorkspaceOneDevices"];
}) => {
  const translate = useTranslate();
  const showHexnode = rows.some(row => row.type === "TV Box");
  const showWorkspaceOne = rows.some(row => row.type === "Tablet");
  const columns = ["providerStatus", "lastOnline", "appVersion", "qualityCheckDoneAt", "lastInStockAt"] as const;
  return (
    <InventoryTable
      label={translate("logistics.inventory.title")}
      maxHeight="calc(100vh - 360px)"
      columns={[
        ...columns.map(key => ({ key, label: translate(`logistics.inventory.columns.${key}`) })),
        ...(showHexnode
          ? [
              {
                key: "hexnode",
                label: translate("logistics.inventory.columns.hexnode"),
                sx: { whiteSpace: "normal", minWidth: 300 },
              },
            ]
          : []),
        ...(showWorkspaceOne
          ? [
              {
                key: "workspaceOne",
                label: translate("logistics.inventory.columns.workspaceOne"),
                sx: { whiteSpace: "normal", minWidth: 340 },
              },
            ]
          : []),
      ]}
      rows={rows.map(row => ({
        key: row.id,
        inventory: row,
        cells: [
          row.simProvider &&
            (row.simProvider.listed ? (
              row.simProvider.status
            ) : (
              <Box component="span" sx={{ color: CRM_COLORS.textWeak }}>
                {translate("logistics.sim.notListed")}
              </Box>
            )),
          row.simProvider?.listed ? simLastOnlineText(translate, row.simProvider) : null,
          row.appVersion,
          row.qualityCheckDoneAt && formatCrmDayOf(row.qualityCheckDoneAt),
          row.lastInStockAt && formatCrmDayOf(row.lastInStockAt),
          ...(showHexnode
            ? [
                row.type === "TV Box" ? (
                  <Box sx={{ display: "grid", gap: "4px" }}>
                    <InventoryHexnode hexnode={row.hexnode} compact />
                    {inventoryHexnodeUrl(row.type, row.hexnode) && (
                      <Link
                        href={inventoryHexnodeUrl(row.type, row.hexnode) ?? undefined}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{ color: CRM_COLORS.link, display: "inline-flex", alignItems: "center", gap: "4px" }}
                      >
                        {translate("logistics.hexnode.open")}
                        <OpenInNewIcon sx={{ fontSize: 14 }} />
                      </Link>
                    )}
                  </Box>
                ) : null,
              ]
            : []),
          ...(showWorkspaceOne
            ? [
                row.type === "Tablet" ? (
                  <Box sx={{ display: "grid", gap: "6px" }}>
                    <InventoryWorkspaceOne workspaceOne={row.workspaceOne} compact />
                    <WorkspaceOneInventoryContext inventoryId={row.id} overview={overview} />
                    {inventoryWorkspaceOneUrl(row.type, row.workspaceOne) && (
                      <Link
                        href={inventoryWorkspaceOneUrl(row.type, row.workspaceOne) ?? undefined}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{ color: CRM_COLORS.link, display: "inline-flex", alignItems: "center", gap: "4px" }}
                      >
                        {translate("logistics.workspaceOne.open")}
                        <OpenInNewIcon sx={{ fontSize: 14 }} />
                      </Link>
                    )}
                  </Box>
                ) : null,
              ]
            : []),
        ],
      }))}
    />
  );
};

const LogisticsInventoryView = () => {
  const translate = useTranslate();
  const [params, setParams] = useSearchParams();
  const type = params.get("type") || null;
  const status = params.get("status") || null;
  const stockState = stockStateOf(params.get("stockState"));
  const deviceFilter = deviceFilterOf(params.get("deviceFilter"));
  const parsedDays = Number(params.get("inactiveDays"));
  const inactiveDays = INACTIVE_DAY_PRESETS.includes(parsedDays) ? parsedDays : DEFAULT_INACTIVE_DAYS;
  const search = params.get("q") ?? "";
  const page = Math.min(MAX_PAGE, Math.max(0, Math.trunc(Number(params.get("page"))) || 0));
  const [searchInput, setSearchInput] = useState(search);

  const update = (changes: Record<string, string | null>) =>
    setParams(
      current => {
        const next = new URLSearchParams(current);
        Object.entries(changes).forEach(([key, value]) => (value ? next.set(key, value) : next.delete(key)));
        return next;
      },
      { replace: true }
    );

  useEffect(() => {
    const timer = window.setTimeout(() => {
      if (searchInput.trim() !== search) update({ q: searchInput.trim() || null, page: null });
    }, SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
    // Only the typed text starts a new search.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchInput]);

  // The address can change the search without typing: the menu entry opens
  // the list without one. Typing must not reset the box.
  useEffect(() => {
    setSearchInput(current => (current.trim() === search ? current : search));
  }, [search]);

  const { data, previousData, loading, error } = useQuery<LogisticsInventoryQuery, LogisticsInventoryQueryVariables>(
    LogisticsInventoryDocument,
    {
      variables: {
        input: {
          type,
          status,
          stockState,
          search: search || null,
          offset: page * PAGE_SIZE,
          ...(deviceFilter ? { deviceFilter } : {}),
          ...(deviceFilter === LogisticsInventoryDeviceFilter.InactiveManaged ? { inactiveDays } : {}),
        },
      },
      fetchPolicy: "cache-and-network",
    }
  );
  const {
    data: fleetData,
    loading: fleetLoading,
    error: fleetError,
  } = useQuery<LogisticsWorkspaceOneDevicesQuery>(LogisticsWorkspaceOneDevicesDocument, {
    skip: type !== "Tablet",
    fetchPolicy: "cache-and-network",
  });
  const fleet = type === "Tablet" ? fleetData?.logisticsWorkspaceOneDevices : undefined;
  const result = (data ?? previousData)?.logisticsInventory;
  const lastPage = result ? Math.max(0, Math.ceil(result.totalCount / PAGE_SIZE) - 1) : 0;

  // A page past the end, from an old address or a list that shrank, shows the last one.
  const pastEnd = data !== undefined && page > lastPage;
  useEffect(() => {
    if (pastEnd) update({ page: lastPage > 0 ? String(lastPage) : null });
    // update is recreated every render; the page and the list decide.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pastEnd, page, lastPage]);
  // Until the last page arrives, the shown result is the empty page past the
  // end of a list that has units: wait, do not say that none matches.
  const waiting =
    pastEnd ||
    (loading && data === undefined && result !== undefined && result.rows.length === 0 && result.totalCount > 0);

  const groups = result ? groupInventoryCounts(result.counts) : [];
  // The address's type and status stay selectable, even before the counts
  // arrive or when no unit has them.
  const types = Array.from(
    new Set([...groups.flatMap(group => (group.type === null ? [] : [group.type])), ...(type ? [type] : [])])
  );
  const statuses = Array.from(
    new Set([
      ...groups
        .filter(group => type === null || group.type === type)
        .flatMap(group => group.statuses.flatMap(entry => (entry.status === null ? [] : [entry.status]))),
      ...(status ? [status] : []),
    ])
  ).sort((a, b) => a.localeCompare(b, "de"));
  // The selects narrow the search. A count, or removing the filters, shows
  // every unit of a type and status, without a search.
  const select = ({ type: nextType, status: nextStatus }: Pick<Selection, "type" | "status">) =>
    update({
      type: nextType,
      status: nextStatus,
      stockState: null,
      page: null,
      ...(deviceFilter === LogisticsInventoryDeviceFilter.WorkspaceOne && nextType !== "Tablet"
        ? { deviceFilter: null, inactiveDays: null }
        : {}),
    });
  const showAllOf = ({ type: nextType, status: nextStatus, stockState: nextStockState }: Selection) => {
    setSearchInput("");
    update({
      type: nextType,
      status: nextStatus,
      stockState: nextStockState,
      q: null,
      page: null,
      deviceFilter: null,
      inactiveDays: null,
    });
  };
  const title = translate("logistics.inventory.title");

  return (
    <Box>
      <Title title={title} />
      <CrmPageHeader
        icon={<Inventory2OutlinedIcon sx={{ fontSize: 32, color: CRM_COLORS.brand }} />}
        overline={translate("logistics.section")}
        title={title}
        meta={
          result && (
            <>
              {translate("logistics.inventory.count", {
                total: formatCrmCount(result.totalCount),
                smart_count: result.totalCount,
              })}
              {" • "}
              {result.syncedAt
                ? translate("logistics.inventory.syncedAt", { time: formatCrmDateTime(result.syncedAt) })
                : translate("logistics.inventory.notSynced")}
            </>
          )
        }
      >
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: "8px", px: "16px", pb: "16px" }}>
          <TextField
            select
            size="small"
            label={translate("logistics.inventory.type")}
            value={type ?? ALL}
            onChange={event => select({ type: event.target.value || null, status: null })}
            sx={{ minWidth: 150, backgroundColor: CRM_COLORS.cardBackground }}
          >
            <MenuItem value={ALL}>{translate("logistics.inventory.all")}</MenuItem>
            {types.map(option => (
              <MenuItem key={option} value={option}>
                <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: "8px" }}>
                  <InventoryTypeIcon type={option} size={18} />
                  {option}
                </Box>
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            size="small"
            label={translate("logistics.inventory.deviceFilters.label")}
            SelectProps={{ displayEmpty: true }}
            InputLabelProps={{ shrink: true }}
            value={deviceFilter === LogisticsInventoryDeviceFilter.InactiveManaged ? ALL : (deviceFilter ?? ALL)}
            onChange={event => {
              const next = deviceFilterOf(event.target.value);
              update({
                deviceFilter: next,
                inactiveDays: null,
                page: null,
                ...(next === LogisticsInventoryDeviceFilter.WorkspaceOne ? { type: "Tablet" } : {}),
              });
            }}
            sx={{ minWidth: 150, backgroundColor: CRM_COLORS.cardBackground }}
          >
            <MenuItem value={ALL}>{translate("logistics.inventory.all")}</MenuItem>
            {[LogisticsInventoryDeviceFilter.WorkspaceOne, LogisticsInventoryDeviceFilter.UnknownLastSeenManaged].map(
              filter => (
                <MenuItem key={filter} value={filter}>
                  {translate(`logistics.inventory.deviceFilters.options.${filter}`)}
                </MenuItem>
              )
            )}
          </TextField>
          <TextField
            select
            size="small"
            label={translate("logistics.inventory.deviceFilters.days")}
            SelectProps={{ displayEmpty: true }}
            InputLabelProps={{ shrink: true }}
            value={deviceFilter === LogisticsInventoryDeviceFilter.InactiveManaged ? inactiveDays : ALL}
            onChange={event => {
              const days = event.target.value;
              if (days) {
                update({
                  deviceFilter: LogisticsInventoryDeviceFilter.InactiveManaged,
                  inactiveDays: days,
                  page: null,
                });
              } else if (deviceFilter === LogisticsInventoryDeviceFilter.InactiveManaged) {
                update({ deviceFilter: null, inactiveDays: null, page: null });
              }
            }}
            sx={{ minWidth: 150, backgroundColor: CRM_COLORS.cardBackground }}
          >
            <MenuItem value={ALL}>{translate("logistics.inventory.all")}</MenuItem>
            {INACTIVE_DAY_PRESETS.map(days => (
              <MenuItem key={days} value={days}>
                {translate("logistics.inventory.deviceFilters.dayCount", { days })}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            size="small"
            label={translate("logistics.inventory.status")}
            value={status ?? ALL}
            onChange={event => select({ type, status: event.target.value || null })}
            sx={{ minWidth: 150, backgroundColor: CRM_COLORS.cardBackground }}
          >
            <MenuItem value={ALL}>{translate("logistics.inventory.all")}</MenuItem>
            {statuses.map(option => (
              <MenuItem key={option} value={option}>
                {option}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            size="small"
            value={searchInput}
            onChange={event => setSearchInput(event.target.value)}
            placeholder={translate("logistics.inventory.searchPlaceholder")}
            inputProps={{ "aria-label": translate("logistics.inventory.searchLabel") }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
              sx: { backgroundColor: CRM_COLORS.cardBackground, fontSize: "13px", width: 260 },
            }}
          />
          {(type || status || stockState || search || deviceFilter) && (
            <Button
              size="small"
              variant="outlined"
              onClick={() => showAllOf({ type: null, status: null, stockState: null })}
              sx={{ textTransform: "none", backgroundColor: CRM_COLORS.cardBackground }}
            >
              {translate("logistics.inventory.clearFilters")}
            </Button>
          )}
        </Box>
        {result && (
          <InventoryCounts
            groups={groups}
            stock={result.stock}
            selected={{ type, status, stockState }}
            onSelect={showAllOf}
          />
        )}
        {deviceFilter && (
          <Typography sx={{ px: "16px", pb: "16px", fontSize: "12px", color: CRM_COLORS.textWeak }}>
            {translate(
              deviceFilter === LogisticsInventoryDeviceFilter.WorkspaceOne
                ? "logistics.inventory.deviceFilters.mdmHelp"
                : "logistics.inventory.deviceFilters.activityHelp"
            )}
          </Typography>
        )}
      </CrmPageHeader>
      {data && deviceFilter && <DeviceFilterCoverage sources={data.logisticsInventory.deviceFilterSources} />}
      {error && <Alert severity="error">{translate("logistics.inventory.loadError")}</Alert>}
      {((loading && !result) || waiting) && <CircularProgress aria-label={translate("logistics.inventory.loading")} />}
      {result && !waiting && (
        <CrmCard flush>
          {result.rows.length === 0 ? (
            <Typography sx={{ p: "16px", fontSize: "13px", color: CRM_COLORS.textWeak }}>
              {translate("logistics.inventory.empty")}
            </Typography>
          ) : (
            <InventoryOverviewTable rows={result.rows} overview={fleet} />
          )}
          {result.totalCount > PAGE_SIZE && (
            <ListPagination
              total={result.totalCount}
              page={page + 1}
              perPage={PAGE_SIZE}
              resource={ResourceName.LogisticsInventory}
              onPageChange={next => update({ page: next > 1 ? String(next - 1) : null })}
              isLoading={loading}
            />
          )}
        </CrmCard>
      )}
      {type === "Tablet" && (
        <WorkspaceOneInventoryOverview overview={fleet} loading={fleetLoading} error={Boolean(fleetError)} />
      )}
    </Box>
  );
};

const InventoryContent = () => {
  const [params] = useSearchParams();
  if (
    params.get("tab") === "mdm-tablets" ||
    (params.get("deviceFilter") === LogisticsInventoryDeviceFilter.WorkspaceOne && params.get("type") !== "Tablet")
  ) {
    const next = new URLSearchParams(params);
    next.delete("tab");
    if (next.get("type") !== "Tablet" || next.get("deviceFilter") !== LogisticsInventoryDeviceFilter.WorkspaceOne)
      next.delete("page");
    next.set("type", "Tablet");
    next.set("deviceFilter", LogisticsInventoryDeviceFilter.WorkspaceOne);
    next.delete("inactiveDays");
    return <Navigate to={{ search: `?${next}` }} replace />;
  }
  return <LogisticsInventoryView />;
};

export const LogisticsInventoryPage = () => (
  <RequirePermission permission={ResourceName.LogisticsInventory}>
    <InventoryContent />
  </RequirePermission>
);
